import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { 
  Mail, 
  Shield, 
  CreditCard, 
  Package, 
  Check, 
  Copy, 
  Loader2,
  Plus,
  Minus,
  ChevronRight,
  Lock,
  Clock,
  Send
} from "lucide-react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreditPackageCard = ({ pkg, selected, onSelect }) => (
  <button
    onClick={() => onSelect(pkg.letters)}
    className={`p-4 border text-center transition-all ${
      selected 
        ? 'border-lws-green bg-lws-green/5' 
        : 'border-lws-ink/10 hover:border-lws-green/50'
    }`}
    data-testid={`credit-package-${pkg.letters}`}
  >
    <div className="text-2xl font-serif text-lws-green">{pkg.letters}</div>
    <div className="text-xs text-lws-taupe mt-1">{pkg.letters === 1 ? 'letter' : 'letters'}</div>
    <div className="text-sm font-medium text-lws-ink mt-2">${pkg.price}</div>
  </button>
);

export const ForwardingPortalPage = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [selectedCredits, setSelectedCredits] = useState(1);
  const [customCredits, setCustomCredits] = useState("");
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const pricingRes = await axios.get(`${BACKEND_URL}/api/forwarding/pricing`);
      setPricing(pricingRes.data);

      if (user) {
        const [statusRes, historyRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/forwarding/status`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/forwarding/history`, { withCredentials: true })
        ]);
        setStatus(statusRes.data);
        setHistory(historyRes.data.logs || []);
      }
    } catch (error) {
      console.error("Error fetching forwarding data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!user) {
      window.location.hash = "#register";
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/forwarding/subscribe`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to start subscription");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handlePurchaseCredits = async () => {
    if (!user) {
      window.location.hash = "#register";
      return;
    }

    const letters = customCredits ? parseInt(customCredits) : selectedCredits;
    if (!letters || letters < 1) {
      alert("Please select at least 1 letter credit");
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/forwarding/credits/purchase`,
        { letters, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to purchase credits");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const copyMemberId = () => {
    if (status?.member_id) {
      navigator.clipboard.writeText(status.member_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-lws-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="forwarding-portal-page">
      {/* Hero */}
      <section className="py-24 px-6 bg-lws-green text-lws-paper">
        <div className="max-w-4xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs uppercase tracking-[0.4em] text-lws-amber mb-6 block"
          >
            Private Forwarding
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-7xl font-serif mb-6"
          >
            Your Address <span className="italic text-lws-amber">Stays Yours</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-lws-paper/80 max-w-2xl mx-auto"
          >
            The letters are real. The privacy is real. Have all correspondence pass through 
            Letter Writers Society — your home address is never shared.
          </motion.p>
        </div>
      </section>

      {/* Member ID Card (if logged in) */}
      {user && status && (
        <section className="py-12 px-6 bg-lws-paper border-b border-lws-ink/10">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white border border-lws-ink/10 p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <p className="text-xs uppercase tracking-wider text-lws-taupe mb-2">Your Member ID</p>
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-serif text-lws-green" data-testid="member-id">
                    {status.member_id}
                  </span>
                  <button
                    onClick={copyMemberId}
                    className="p-2 hover:bg-lws-green/10 rounded transition-colors"
                    data-testid="copy-member-id"
                  >
                    {copied ? <Check className="w-5 h-5 text-lws-green" /> : <Copy className="w-5 h-5 text-lws-taupe" />}
                  </button>
                </div>
                <p className="text-sm text-lws-taupe mt-2">
                  Use this ID on envelopes: <strong>{status.member_id}</strong>, c/o Letter Writers Society
                </p>
              </div>
              
              <div className="text-center md:text-right">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm ${
                  status.forwarding_active 
                    ? 'bg-lws-green/10 text-lws-green' 
                    : 'bg-lws-ink/5 text-lws-taupe'
                }`}>
                  {status.forwarding_active ? (
                    <>
                      <Check className="w-4 h-4" />
                      Private Forwarding Active
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      Forwarding Inactive
                    </>
                  )}
                </div>
                {status.letter_credits > 0 && (
                  <p className="text-sm text-lws-green mt-2" data-testid="credit-balance">
                    {status.credit_message}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-serif text-center text-lws-ink mb-16">
            How Private Forwarding Works
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-lws-green/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Send className="w-7 h-7 text-lws-green" />
              </div>
              <h3 className="text-lg font-serif text-lws-ink mb-3">1. You Write</h3>
              <p className="text-lws-taupe text-sm">
                Address your letter to your pen pal&apos;s member ID, care of Letter Writers Society.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-lws-amber/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Package className="w-7 h-7 text-lws-amber" />
              </div>
              <h3 className="text-lg font-serif text-lws-ink mb-3">2. We Forward</h3>
              <p className="text-lws-taupe text-sm">
                We receive your letter, re-envelope it with your pen pal&apos;s real address, and post it on.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-lws-rose/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-7 h-7 text-lws-rose" />
              </div>
              <h3 className="text-lg font-serif text-lws-ink mb-3">3. They Receive</h3>
              <p className="text-lws-taupe text-sm">
                A real letter, a real connection — with no addresses exchanged between members.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 px-6 bg-lws-paper border-y border-lws-ink/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-serif text-center text-lws-ink mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-center text-lws-taupe mb-16 max-w-xl mx-auto">
            Both sender and recipient need Private Forwarding active. Then each letter is charged a flat fee.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            {/* Subscription Card */}
            <div className="bg-white border border-lws-ink/10 p-8">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-lws-green" />
                <h3 className="text-xl font-serif text-lws-ink">Private Forwarding</h3>
              </div>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-serif text-lws-green">${pricing?.monthly_fee || 6}</span>
                <span className="text-lws-taupe">/month</span>
              </div>
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-start gap-2 text-lws-ink">
                  <Check className="w-4 h-4 text-lws-green mt-0.5 flex-shrink-0" />
                  Your home address never shared
                </li>
                <li className="flex items-start gap-2 text-lws-ink">
                  <Check className="w-4 h-4 text-lws-green mt-0.5 flex-shrink-0" />
                  Unique member ID for envelopes
                </li>
                <li className="flex items-start gap-2 text-lws-ink">
                  <Check className="w-4 h-4 text-lws-green mt-0.5 flex-shrink-0" />
                  Same-week forwarding turnaround
                </li>
                <li className="flex items-start gap-2 text-lws-ink">
                  <Check className="w-4 h-4 text-lws-green mt-0.5 flex-shrink-0" />
                  Addresses stored securely
                </li>
              </ul>
              
              {status?.forwarding_active ? (
                <button
                  disabled
                  className="w-full py-3 bg-lws-green/10 text-lws-green text-xs uppercase tracking-wider"
                >
                  <Check className="w-4 h-4 inline mr-2" />
                  Subscribed
                </button>
              ) : (
                <button
                  onClick={handleSubscribe}
                  disabled={checkoutLoading}
                  className="w-full py-3 bg-lws-green text-lws-paper text-xs uppercase tracking-wider hover:bg-lws-navy transition-colors disabled:opacity-50"
                  data-testid="subscribe-btn"
                >
                  {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Subscribe Now"}
                </button>
              )}
            </div>
            
            {/* Per Letter Card */}
            <div className="bg-white border border-lws-ink/10 p-8">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="w-6 h-6 text-lws-amber" />
                <h3 className="text-xl font-serif text-lws-ink">Per Letter</h3>
              </div>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-serif text-lws-green">${pricing?.per_letter_fee || 5}</span>
                <span className="text-lws-taupe">/letter</span>
              </div>
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-start gap-2 text-lws-ink">
                  <Check className="w-4 h-4 text-lws-green mt-0.5 flex-shrink-0" />
                  Flat fee — domestic or international
                </li>
                <li className="flex items-start gap-2 text-lws-ink">
                  <Check className="w-4 h-4 text-lws-green mt-0.5 flex-shrink-0" />
                  Prepaid credit system
                </li>
                <li className="flex items-start gap-2 text-lws-ink">
                  <Check className="w-4 h-4 text-lws-green mt-0.5 flex-shrink-0" />
                  Buy in bulk &amp; save time
                </li>
                <li className="flex items-start gap-2 text-lws-ink">
                  <Check className="w-4 h-4 text-lws-green mt-0.5 flex-shrink-0" />
                  Credits never expire
                </li>
              </ul>
              
              <p className="text-xs text-lws-taupe text-center">
                Purchase credits below ↓
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credit Purchase */}
      {user && status && (
        <section className="py-24 px-6">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-serif text-center text-lws-ink mb-4">
              Top Up Your Letter Credits
            </h2>
            <p className="text-center text-lws-taupe mb-12">
              Current balance: <strong className="text-lws-green">{status.letter_credits} {status.letter_credits === 1 ? 'letter' : 'letters'}</strong>
            </p>
            
            {/* Quick Packages */}
            <div className="grid grid-cols-4 gap-4 mb-8">
              {pricing?.credit_packages?.map((pkg) => (
                <CreditPackageCard
                  key={pkg.letters}
                  pkg={pkg}
                  selected={selectedCredits === pkg.letters && !customCredits}
                  onSelect={(l) => { setSelectedCredits(l); setCustomCredits(""); }}
                />
              ))}
            </div>
            
            {/* Custom Amount */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-lws-taupe text-sm">Or choose custom:</span>
              <div className="flex items-center border border-lws-ink/20 rounded">
                <button 
                  onClick={() => setCustomCredits(Math.max(1, (parseInt(customCredits) || 1) - 1).toString())}
                  className="p-2 hover:bg-lws-ink/5"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  min="1"
                  value={customCredits}
                  onChange={(e) => setCustomCredits(e.target.value)}
                  placeholder="0"
                  className="w-16 text-center py-2 border-0 focus:ring-0"
                  data-testid="custom-credits-input"
                />
                <button 
                  onClick={() => setCustomCredits(((parseInt(customCredits) || 0) + 1).toString())}
                  className="p-2 hover:bg-lws-ink/5"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <span className="text-lws-taupe text-sm">
                letters = <strong className="text-lws-green">
                  ${(customCredits ? parseInt(customCredits) : selectedCredits) * (pricing?.per_letter_fee || 5)}
                </strong>
              </span>
            </div>
            
            <button
              onClick={handlePurchaseCredits}
              disabled={checkoutLoading || !status.forwarding_active}
              className="w-full py-4 bg-lws-green text-lws-paper text-xs uppercase tracking-wider hover:bg-lws-navy transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="purchase-credits-btn"
            >
              {!status.forwarding_active ? (
                "Subscribe to Private Forwarding First"
              ) : checkoutLoading ? (
                <Loader2 className="w-4 h-4 animate-spin inline" />
              ) : (
                `Purchase ${customCredits ? parseInt(customCredits) : selectedCredits} Letter${(customCredits ? parseInt(customCredits) : selectedCredits) === 1 ? '' : 's'}`
              )}
            </button>
          </div>
        </section>
      )}

      {/* Forwarding History */}
      {user && history.length > 0 && (
        <section className="py-24 px-6 bg-lws-paper border-t border-lws-ink/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-serif text-center text-lws-ink mb-12">
              Your Forwarding History
            </h2>
            
            <div className="space-y-4">
              {history.map((log) => (
                <div 
                  key={log.log_id} 
                  className="bg-white border border-lws-ink/10 p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      log.sender_member_id === status?.member_id 
                        ? 'bg-lws-amber/20 text-lws-amber' 
                        : 'bg-lws-green/10 text-lws-green'
                    }`}>
                      {log.sender_member_id === status?.member_id ? (
                        <Send className="w-5 h-5" />
                      ) : (
                        <Mail className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <p className="text-lws-ink font-medium">
                        {log.sender_member_id === status?.member_id 
                          ? `Sent to ${log.recipient_member_id}`
                          : `Received from ${log.sender_member_id}`
                        }
                      </p>
                      <p className="text-xs text-lws-taupe">
                        {new Date(log.date_received).toLocaleDateString()} • {log.is_international ? 'International' : 'Domestic'}
                      </p>
                    </div>
                  </div>
                  <div className={`px-3 py-1 text-xs uppercase tracking-wider rounded-full ${
                    log.status === 'forwarded' 
                      ? 'bg-lws-green/10 text-lws-green'
                      : log.status === 'received'
                        ? 'bg-lws-amber/10 text-lws-amber'
                        : 'bg-lws-ink/5 text-lws-taupe'
                  }`}>
                    {log.status}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LWS Address */}
      <section className="py-24 px-6 bg-lws-green text-lws-paper">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-serif mb-6">Where to Send Your Letters</h2>
          <div className="bg-lws-paper/10 backdrop-blur p-8 rounded-lg mb-8">
            <p className="text-lg font-mono">
              [Recipient&apos;s Member ID]<br />
              c/o Letter Writers Society<br />
              {pricing?.lws_address?.split(',').map((line, i) => (
                <span key={i}>{line.trim()}<br /></span>
              ))}
            </p>
          </div>
          <p className="text-lws-paper/80">
            Only Frederica and a small trusted team handle your letters.<br />
            Your privacy is our promise.
          </p>
        </div>
      </section>

      {/* CTA for non-logged in users */}
      {!user && (
        <section className="py-24 px-6 text-center">
          <h2 className="text-3xl font-serif text-lws-ink mb-6">Ready to protect your privacy?</h2>
          <a
            href="#register"
            className="inline-flex items-center px-8 py-4 bg-lws-green text-lws-paper text-xs uppercase tracking-wider hover:bg-lws-navy transition-colors"
            data-testid="forwarding-register-cta"
          >
            Create Free Account <ChevronRight className="w-4 h-4 ml-2" />
          </a>
        </section>
      )}

      {checkoutLoading && (
        <div className="fixed inset-0 bg-lws-ink/50 flex items-center justify-center z-50">
          <div className="bg-lws-paper p-8 rounded-lg flex flex-col items-center">
            <Loader2 className="w-8 h-8 animate-spin text-lws-green mb-4" />
            <p className="text-lws-ink">Preparing checkout...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export const ForwardingSuccessPage = () => {
  const [status, setStatus] = useState("checking");
  const [result, setResult] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split("?")[1]);
    const sessionId = params.get("session_id");
    const letters = params.get("letters");
    
    if (sessionId) {
      checkPayment(sessionId, letters);
    } else {
      setStatus("error");
    }
  }, []);

  const checkPayment = async (sessionId, letters) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/forwarding/credits/check/${sessionId}`,
        { withCredentials: true }
      );
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
        setResult(response.data);
      } else {
        // Retry after delay
        setTimeout(() => checkPayment(sessionId, letters), 2000);
      }
    } catch (error) {
      console.error("Error checking payment:", error);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="forwarding-success-page">
      <div className="max-w-lg text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-lws-green mx-auto mb-6" />
            <h1 className="text-3xl font-serif text-lws-ink mb-4">Processing...</h1>
            <p className="text-lws-taupe">Please wait while we confirm your payment.</p>
          </>
        )}

        {status === "success" && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-20 h-20 bg-lws-green rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <Check className="w-10 h-10 text-lws-paper" />
            </motion.div>
            <h1 className="text-3xl font-serif text-lws-ink mb-4">
              {result?.letters_added ? "Credits Added!" : "Subscription Active!"}
            </h1>
            {result?.letters_added ? (
              <p className="text-lws-taupe mb-4">
                {result.message}
              </p>
            ) : (
              <p className="text-lws-taupe mb-4">
                Your Private Forwarding subscription is now active. You can now purchase letter credits.
              </p>
            )}
            <a
              href="#forwarding"
              className="inline-flex items-center px-6 py-3 bg-lws-green text-lws-paper text-xs uppercase tracking-wider hover:bg-lws-navy transition-colors"
            >
              Back to Forwarding Portal
            </a>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-20 h-20 bg-lws-rose/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl text-lws-rose">!</span>
            </div>
            <h1 className="text-3xl font-serif text-lws-ink mb-4">Something went wrong</h1>
            <p className="text-lws-taupe mb-8">
              We couldn&apos;t verify your payment. Please contact support if you were charged.
            </p>
            <a
              href="#forwarding"
              className="inline-flex items-center px-6 py-3 bg-lws-green text-lws-paper text-xs uppercase tracking-wider"
            >
              Back to Forwarding Portal
            </a>
          </>
        )}
      </div>
    </div>
  );
};

export default ForwardingPortalPage;
